import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button, Control } from "./components";

export interface PreviewUser {
  id: string;
  name: string;
}

/** Selection stays in the workspace; the child receives only the chosen preview. */
export function ImpersonationPicker({
  users,
  onSelect,
  onClose,
  description,
  loading = false,
  blocked = false,
  error,
  versions,
  versionId,
  onVersionChange,
  parts,
  partId,
  onPartChange,
}: {
  users: PreviewUser[];
  onSelect: (id: string | null) => void;
  onClose: () => void;
  description?: string;
  loading?: boolean;
  blocked?: boolean;
  error?: string;
  versions?: { id: string; version: number }[];
  versionId?: string;
  onVersionChange?: (id: string) => void;
  parts?: { id: string; name: string }[];
  partId?: string;
  onPartChange?: (id: string) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState("");
  const close = () => {
    dialog.current?.close();
    onClose();
  };
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    return () => element.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="impersonation-picker"
      aria-labelledby="impersonation-picker-title"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <header>
        <div>
          <span className="eyebrow">CUSTOMER PREVIEW</span>
          <h2 id="impersonation-picker-title">
            Who would you like to impersonate?
          </h2>
        </div>
        <button
          className="icon-button"
          aria-label="Close dialog"
          onClick={close}
        >
          <X size={20} />
        </button>
      </header>
      <p>
        {description ||
          "Open the proofing page in a new tab with its personalization form."}
      </p>
      {loading && (
        <p role="status">Loading available users and document versions…</p>
      )}
      {error && <p role="alert">{error}</p>}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (loading || blocked || (versions && !versionId)) return;
          onSelect(selected || null);
        }}
      >
        <Control label="User to impersonate">
          <select
            disabled={loading}
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
            autoFocus
          >
            <option value="">Nobody · no user profile</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </Control>
        {versions && (
          <Control label="Published version">
            <select
              disabled={loading}
              value={versionId || ""}
              onChange={(event) => onVersionChange?.(event.target.value)}
            >
              {!versions.length && (
                <option value="">No published versions</option>
              )}
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  Published v{version.version}
                </option>
              ))}
            </select>
          </Control>
        )}
        {!!parts?.length && (
          <Control label="Preview part">
            <select
              value={partId || ""}
              onChange={(event) => onPartChange?.(event.target.value)}
            >
              <option value="">Main project</option>
              {parts.map((part) => (
                <option key={part.id} value={part.id}>
                  {part.name}
                </option>
              ))}
            </select>
          </Control>
        )}
        <footer>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            kind="primary"
            disabled={loading || blocked || (!!versions && !versionId)}
          >
            Open proofing page
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
